import { firstName, roleOptionLabel } from "../labels";

const snapshot = (user) => ({ id: user.id, name: user.name, email: user.email, role: user.role ?? null, companyIds: user.companyIds, updatedAt: user.updatedAt });
const entryFor = (staged, user) => staged[user.id] || { user: snapshot(user), add: [], remove: [] };
const store = (staged, userId, entry) => {
  const next = { ...staged };
  if (!("role" in entry) && entry.add.length === 0 && entry.remove.length === 0) delete next[userId];
  else next[userId] = entry;
  return next;
};

export const setRole = (staged, user, role) => {
  const entry = { ...entryFor(staged, user) };
  if ((role ?? null) === (entry.user.role ?? null)) delete entry.role;
  else entry.role = role ?? null;
  return store(staged, user.id, entry);
};

export const toggleCompany = (staged, user, companyId) => {
  const entry = entryFor(staged, user);
  const wasStaged = entry.add.includes(companyId) || entry.remove.includes(companyId);
  let add = entry.add.filter((id) => id !== companyId);
  let remove = entry.remove.filter((id) => id !== companyId);
  if (!wasStaged) {
    if (entry.user.companyIds.includes(companyId)) remove = [...remove, companyId];
    else add = [...add, companyId];
  }
  return store(staged, user.id, { ...entry, add, remove });
};

export const effectiveRole = (staged, user) => {
  const entry = staged[user.id];
  return entry && "role" in entry ? entry.role : user.role ?? null;
};
export const isRoleStaged = (staged, userId) => Boolean(staged[userId] && "role" in staged[userId]);

export const cellState = (staged, user, companyId) => {
  const entry = staged[user.id];
  if (entry?.add.includes(companyId)) return "add";
  if (entry?.remove.includes(companyId)) return "remove";
  return user.companyIds.includes(companyId) ? "on" : "off";
};

export const countChanges = (staged) =>
  Object.values(staged).reduce((n, e) => n + ("role" in e ? 1 : 0) + e.add.length + e.remove.length, 0);

export const managersCount = (company, staged) =>
  Object.values(staged).reduce(
    (n, e) => n + (e.add.includes(company.id) ? 1 : 0) - (e.remove.includes(company.id) ? 1 : 0),
    Number(company.managersCount) || 0
  );

// Payload de POST antecipeadmin/access/changes (spec §6.2). `role` só vai quando mudou (null = tirar o papel).
export const toChanges = (staged) =>
  Object.values(staged)
    .sort((a, b) => a.user.id - b.user.id)
    .map((e) => {
      const change = { userId: e.user.id, expectedUpdatedAt: e.user.updatedAt, addCompanyIds: e.add, removeCompanyIds: e.remove };
      if ("role" in e) change.role = e.role;
      return change;
    });

export const dropUsers = (staged, userIds) => {
  const next = { ...staged };
  (userIds || []).forEach((id) => delete next[id]);
  return next;
};

export const describeChanges = (staged, companiesById) =>
  Object.values(staged)
    .sort((a, b) => a.user.id - b.user.id)
    .map((e) => ({
      user: e.user,
      role: "role" in e ? { from: e.user.role ?? null, to: e.role } : undefined,
      add: e.add.map((id) => companiesById.get(id) || `#${id}`),
      remove: e.remove.map((id) => companiesById.get(id) || `#${id}`),
    }));

const who = (user) => firstName(user.name) || user.email;

export const summaryText = (items) =>
  items
    .flatMap((item) => [
      ...item.add.map((name) => `${who(item.user)} +${name}`),
      ...(item.role ? [`${who(item.user)} → ${roleOptionLabel(item.role.to)}`] : []),
      ...item.remove.map((name) => `${who(item.user)} −${name}`),
    ])
    .join(" · ");

export const consequencesText = (items) => {
  const parts = items.flatMap((item) => {
    const n = who(item.user);
    const out = [];
    if (item.role) {
      const { from, to } = item.role;
      if (from === null && to !== null) out.push(`${n} passa a ver a seção "Antecipe" no menu`);
      if (from !== null && to === null) out.push(`${n} deixa de ver a seção "Antecipe" no menu`);
      if (to === "admin" && from !== "admin") out.push(`${n} passa a ver a seção "Administração"`);
      if (from === "admin" && to !== "admin") out.push(`${n} deixa de ver a seção "Administração"`);
    }
    if (item.add.length) out.push(`${n} passa a ver pacotes da ${item.add.join(", ")}`);
    if (item.remove.length) out.push(`${n} deixa de ver pacotes da ${item.remove.join(", ")}`);
    return out;
  });
  return `Vale no próximo clique de cada pessoa.${parts.length ? ` ${parts.join("; ")}.` : ""}`;
};
